// src/components/Shared/TermsModal.jsx
//
// The terms, shown over the signup form instead of navigating to /terms.
// Sending someone to a full page mid-signup threw away everything they had
// already typed, which is a good way to lose a signup at the last step.
//
// The text is the same TermsContent the /terms page renders, so the two can
// never drift apart.

import React, { useEffect, useRef } from 'react';
import { TermsContent } from '../Landing/TermsPage';

const TermsModal = ({ onClose, onAccept }) => {
	const closeRef = useRef(null);

	useEffect(() => {
		// Escape closes, and the page behind must not scroll while this is open.
		const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
		document.addEventListener('keydown', onKey);
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		closeRef.current?.focus();
		return () => {
			document.removeEventListener('keydown', onKey);
			document.body.style.overflow = prevOverflow;
		};
	}, [onClose]);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Terms and conditions"
			onClick={onClose}
			style={{
				position: 'fixed',
				inset: 0,
				background: 'rgba(17, 17, 19, 0.45)',
				backdropFilter: 'blur(2px)',
				zIndex: 9100,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: 16,
			}}
		>
			<div
				onClick={(e) => e.stopPropagation()}
				style={{
					background: 'white',
					maxWidth: 680,
					width: '100%',
					maxHeight: '88vh',
					borderRadius: 16,
					boxShadow: '0 30px 60px -10px rgba(17,17,19,0.25)',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 12,
						padding: '18px 20px',
						borderBottom: '1px solid rgba(17,17,19,0.10)',
						flexShrink: 0,
					}}
				>
					<strong style={{ fontFamily: "'Poppins', sans-serif", fontSize: 18 }}>
						Terms of Service
					</strong>
					<button
						ref={closeRef}
						type="button"
						className="ev-soft-btn"
						onClick={onClose}
						aria-label="Close terms"
					>
						Close
					</button>
				</div>

				{/* Only this scrolls, so the accept button stays reachable. */}
				<div style={{ overflowY: 'auto', padding: '4px 20px 8px', flex: 1, minHeight: 0 }}>
					<TermsContent />
				</div>

				<div
					style={{
						padding: '14px 20px',
						borderTop: '1px solid rgba(17,17,19,0.10)',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 10,
						flexShrink: 0,
					}}
				>
					<button type="button" className="ev-soft-btn" onClick={onClose}>
						Cancel
					</button>
					<button type="button" className="ev-primary-btn" onClick={onAccept}>
						I agree
					</button>
				</div>
			</div>
		</div>
	);
};

export default TermsModal;
